import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemText, Paper, Tab, Table,
    TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography
} from '@mui/material';
import { extractErrorMessage } from '../Services/httpClient';
import { canApproveCatalog, canEditCatalog, getCurrentUserId, hasRole, ROLES } from '../Services/AuthService';
import {
    approveVersion, cancelSchedule, deactivateItem, eventLabel, getItem, getItemHistory, listItems, rejectVersion, requestPublication, revertItem, saveDraft
} from '../Services/CatalogApi';
import {
    activeVersion, canDeactivate, CONSENT_LABELS, consentChanges, diffContent, formatValue, inFlightVersion, itemStatus, publishedGroupKeys, STATE_COLORS,
    STATE_LABELS, versionActions
} from '../Services/catalogModel';
import TestSendDialog from './TestSendDialog';
import TypeContentEditor from './TypeContentEditor';
import useCatalogLimits from './useCatalogLimits';

const ORANGE = '#f77513';
const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '');
const shortId = (id) => (id ? id.slice(0, 8) : '');
const pretty = (value) => JSON.stringify(value ?? {}, null, 2);
const sortedVersions = (item) => [...item.versions].sort((a, b) => b.number - a.number);

/**
 * Um item do catálogo (tipo ou grupo): versões, conteúdo, comparação, histórico e as ações que o perfil permite. A tela só esconde o que a pessoa
 * não pode fazer; quem decide de verdade é o servidor, e o erro dele aparece aqui. O conteúdo se edita em `TypeContentEditor` (atributos, canais,
 * dados e, para o que o formulário não cobre, o JSON).
 */
const CatalogItemDialog = ({ itemId, onClose, onChanged }) => {
    const [item, setItem] = useState(null);
    const [events, setEvents] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [tab, setTab] = useState(0);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(null);         // o conteúdo em edição (objeto)
    const [groups, setGroups] = useState(null);       // ids dos grupos de limite publicados, para conferir o grupo do tipo
    const [summary, setSummary] = useState('');
    const [prompt, setPrompt] = useState(null);   // { kind: 'reject' | 'revert' | 'deactivate' | 'schedule', value }
    const [testing, setTesting] = useState(false);   // o diálogo de envio de teste está aberto
    const limits = useCatalogLimits();

    const ctx = { canEdit: canEditCatalog(), canApprove: canApproveCatalog(), myId: getCurrentUserId() };

    const load = useCallback(async (keepSelection) => {
        setError(null);
        try {
            const [loaded, history] = await Promise.all([getItem(itemId), getItemHistory(itemId)]);
            setItem(loaded);
            setEvents(history);
            setSelectedId((current) => {
                if (keepSelection && current && loaded.versions.some((v) => v.id === current)) return current;
                return (inFlightVersion(loaded) ?? activeVersion(loaded) ?? sortedVersions(loaded)[0])?.id ?? null;
            });
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    }, [itemId]);

    useEffect(() => { load(false); }, [load]);

    const selected = item?.versions.find((v) => v.id === selectedId) ?? null;
    const actions = selected ? versionActions(selected, item, ctx) : {};

    useEffect(() => {
        setEditing(false);
        setDraft(null);
        setSummary('');
    }, [selectedId]);

    const startEditing = () => {
        setError(null);
        setInfo(null);
        setDraft(selected.content ?? {});
        setEditing(true);
        setTab(0);
        if (item.kind === 'type') {
            listItems().then((all) => setGroups(publishedGroupKeys(all))).catch(() => setGroups(null));   // sem a lista, a tela não acusa o grupo
        }
    };

    // O que mudaria nos atributos de consentimento em relação ao que está salvo: avisa antes de enviar quem não é editor de consentimento.
    const pendingConsent = useMemo(
        () => (editing && draft && selected ? consentChanges(selected.content, draft) : []),
        [editing, draft, selected]);
    const consentBlocked = pendingConsent.length > 0 && !hasRole(ROLES.consentEditor);

    const act = async (fn, message, keep = true) => {
        setBusy(true);
        setError(null);
        setInfo(null);
        try {
            const result = await fn();
            setInfo(message);
            setPrompt(null);
            setEditing(false);
            await load(keep);
            if (result?.id && !keep) setSelectedId(result.id);
            onChanged?.();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const handleSave = () => {
        if (!draft) return;
        act(() => saveDraft(item.id, draft, summary || null), 'Rascunho salvo.', false);
    };

    const compareTarget = useMemo(() => {
        if (!item || !selected) return null;
        const active = activeVersion(item);
        if (active && active.id !== selected.id) return active;
        return sortedVersions(item).find((v) => v.number < selected.number) ?? null;
    }, [item, selected]);

    const diff = useMemo(
        () => (selected && compareTarget ? diffContent(compareTarget.content, selected.content) : []),
        [selected, compareTarget]);

    const submitPrompt = () => {
        const reason = prompt.value?.trim();
        if (prompt.kind === 'reject') return act(() => rejectVersion(selected.id, reason), 'Versão rejeitada.');
        if (prompt.kind === 'revert') return act(() => revertItem(item.id, selected.id, reason), 'Versão anterior republicada.', false);
        if (prompt.kind === 'deactivate') return act(() => deactivateItem(item.id, reason), 'Item desativado. O envio parou.');
        if (prompt.kind === 'schedule') {
            const when = new Date(prompt.value);
            if (Number.isNaN(when.getTime()) || when <= new Date()) { setError('Escolha uma data e hora no futuro.'); return null; }
            return act(() => approveVersion(selected.id, when.toISOString()), 'Versão aprovada e agendada.');
        }
        return null;
    };

    const status = item ? itemStatus(item) : null;

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {item ? item.key : 'Carregando...'}
                {item && <Chip size="small" label={item.kind === 'type' ? 'Tipo de notificação' : 'Grupo de limite'} />}
                {status && <Chip size="small" label={status.label} color={status.color} />}
                <Box sx={{ flexGrow: 1 }} />
                {item && canDeactivate(item, ctx) && (
                    <Button size="small" color="error" variant="outlined" onClick={() => setPrompt({ kind: 'deactivate', value: '' })}>
                        Desativar (parar o envio)
                    </Button>
                )}
            </DialogTitle>

            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}
                {item?.isDeactivated && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Este item está desativado{item.deactivateReason ? `: ${item.deactivateReason}` : ''}. Para voltar ao ar, publique uma versão pelo fluxo normal.
                    </Alert>
                )}
                {!item && !error && <CircularProgress size={24} style={{ color: ORANGE }} />}

                {item && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Paper variant="outlined" sx={{ width: 260, flexShrink: 0, borderRadius: '14px' }}>
                            <Typography variant="subtitle2" sx={{ p: 1.5, color: ORANGE, fontWeight: 'bold' }}>Versões</Typography>
                            <List dense disablePadding>
                                {sortedVersions(item).map((v) => (
                                    <ListItemButton key={v.id} selected={v.id === selectedId} onClick={() => setSelectedId(v.id)}>
                                        <ListItemText
                                            primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <b>v{v.number}</b>
                                                <Chip size="small" label={STATE_LABELS[v.state] || v.state} color={STATE_COLORS[v.state] || 'default'} />
                                            </Box>}
                                            secondary={`${formatDate(v.updatedAt)} · ${v.id === item.activeVersionId ? 'no ar' : ''}`}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Paper>

                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            {selected && (
                                <>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                        {(actions.edit || actions.startNew) && !editing && (
                                            <Button size="small" variant="outlined" style={{ color: ORANGE, borderColor: ORANGE }}
                                                onClick={startEditing}>
                                                {actions.startNew ? 'Nova versão a partir desta' : 'Editar'}
                                            </Button>
                                        )}
                                        {actions.request && !editing && (
                                            <Button size="small" variant="contained" disabled={busy} style={{ backgroundColor: ORANGE, color: 'white' }}
                                                onClick={() => act(() => requestPublication(selected.id), 'Enviada para aprovação.')}>
                                                Pedir publicação
                                            </Button>
                                        )}
                                        {actions.test && !editing && (
                                            <Button size="small" variant="outlined" disabled={busy} style={{ color: ORANGE, borderColor: ORANGE }}
                                                onClick={() => setTesting(true)}>
                                                Enviar teste
                                            </Button>
                                        )}
                                        {actions.approve && (
                                            <>
                                                <Button size="small" variant="contained" color="success" disabled={busy}
                                                    onClick={() => act(() => approveVersion(selected.id, null), 'Versão aprovada e publicada.')}>
                                                    Aprovar e publicar agora
                                                </Button>
                                                <Button size="small" variant="outlined" color="success" disabled={busy}
                                                    onClick={() => setPrompt({ kind: 'schedule', value: '' })}>
                                                    Aprovar e agendar
                                                </Button>
                                            </>
                                        )}
                                        {actions.reject && (
                                            <Button size="small" variant="outlined" color="error" disabled={busy} onClick={() => setPrompt({ kind: 'reject', value: '' })}>
                                                Rejeitar
                                            </Button>
                                        )}
                                        {actions.cancelSchedule && (
                                            <Button size="small" variant="outlined" disabled={busy} style={{ color: ORANGE, borderColor: ORANGE }}
                                                onClick={() => act(() => cancelSchedule(selected.id), 'Agendamento cancelado. A versão voltou a aguardar aprovação.')}>
                                                Cancelar agendamento
                                            </Button>
                                        )}
                                        {actions.revert && (
                                            <Button size="small" variant="outlined" disabled={busy} style={{ color: ORANGE, borderColor: ORANGE }}
                                                onClick={() => setPrompt({ kind: 'revert', value: '' })}>
                                                Republicar esta versão
                                            </Button>
                                        )}
                                    </Box>

                                    {actions.blockedApprovalReason && <Alert severity="info" sx={{ mb: 1.5 }}>{actions.blockedApprovalReason}</Alert>}
                                    {selected.state === 'rejected' && selected.rejectReason && (
                                        <Alert severity="error" sx={{ mb: 1.5 }}>Rejeitada: {selected.rejectReason}</Alert>
                                    )}
                                    {selected.state === 'scheduled' && (
                                        <Alert severity="info" sx={{ mb: 1.5 }}>Entra no ar em {formatDate(selected.publishAt)}.</Alert>
                                    )}

                                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5 }} TabIndicatorProps={{ style: { backgroundColor: ORANGE } }}>
                                        <Tab label="Conteúdo" />
                                        <Tab label={compareTarget ? `Comparar com v${compareTarget.number}` : 'Comparar'} disabled={!compareTarget} />
                                        <Tab label="Histórico" />
                                    </Tabs>

                                    {tab === 0 && (
                                        editing ? (
                                            <Box>
                                                {pendingConsent.length > 0 && (
                                                    <Alert severity={consentBlocked ? 'warning' : 'info'} sx={{ mb: 1 }}>
                                                        Mexe em consentimento: {pendingConsent.map((c) => CONSENT_LABELS[c.path.split('.')[0]] || c.path).join(', ')}.
                                                        {consentBlocked && ' Só o perfil de editor de consentimento pode salvar isto.'}
                                                    </Alert>
                                                )}
                                                <TypeContentEditor kind={item.kind} value={draft ?? {}} onChange={setDraft} groups={groups} canEditConsent={hasRole(ROLES.consentEditor)} limits={limits} />
                                                <TextField label="O que mudou (opcional)" size="small" fullWidth sx={{ mt: 1.5 }} value={summary} onChange={(e) => setSummary(e.target.value)} />
                                                <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                                                    <Button variant="contained" disabled={busy || !draft || consentBlocked} style={{ backgroundColor: ORANGE, color: 'white' }} onClick={handleSave}>
                                                        Salvar rascunho
                                                    </Button>
                                                    <Button onClick={() => { setEditing(false); setDraft(null); }} style={{ color: '#777' }}>Cancelar</Button>
                                                </Box>
                                                {selected.state !== 'draft' && selected.state !== 'published' && selected.state !== 'superseded' && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                        Editar uma versão que aguarda aprovação ou está agendada a devolve a rascunho e exige nova aprovação.
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: '14px', backgroundColor: '#fafafa' }}>
                                                <pre style={{ margin: 0, fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{pretty(selected.content)}</pre>
                                                {selected.changeSummary && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Resumo: {selected.changeSummary}</Typography>
                                                )}
                                            </Paper>
                                        )
                                    )}

                                    {tab === 1 && compareTarget && (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow style={{ backgroundColor: '#fff4ec' }}>
                                                    <TableCell>Campo</TableCell><TableCell>v{compareTarget.number}</TableCell><TableCell>v{selected.number}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {diff.length === 0 && (
                                                    <TableRow><TableCell colSpan={3} align="center" style={{ color: '#aaa' }}>Sem diferenças</TableCell></TableRow>
                                                )}
                                                {diff.map((d) => (
                                                    <TableRow key={d.path} style={d.consent ? { backgroundColor: '#fff3cd' } : undefined}>
                                                        <TableCell>
                                                            {d.path}
                                                            {d.consent && <Chip size="small" color="warning" label="consentimento" sx={{ ml: 1 }} />}
                                                        </TableCell>
                                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatValue(d.before)}</TableCell>
                                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatValue(d.after)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}

                                    {tab === 2 && (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow style={{ backgroundColor: '#fff4ec' }}>
                                                    <TableCell>Quando</TableCell><TableCell>O quê</TableCell><TableCell>Versão</TableCell><TableCell>Quem</TableCell><TableCell>Motivo</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {events.map((e) => {
                                                    const number = item.versions.find((v) => v.id === e.versionId)?.number;
                                                    return (
                                                        <TableRow key={e.id}>
                                                            <TableCell>{formatDate(e.at)}</TableCell>
                                                            <TableCell>
                                                                {eventLabel(e.action)}
                                                                {e.selfApproved && <Chip size="small" color="warning" label="autoaprovada" sx={{ ml: 1 }} />}
                                                            </TableCell>
                                                            <TableCell>{number ? `v${number}` : ''}</TableCell>
                                                            <TableCell title={e.actorId}>{shortId(e.actorId)}</TableCell>
                                                            <TableCell>{e.reason}</TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions><Button onClick={onClose} style={{ color: ORANGE }}>Fechar</Button></DialogActions>

            {testing && selected && (
                <TestSendDialog version={selected} itemKey={item.key} onClose={() => setTesting(false)} onDone={() => load(true)} />
            )}

            <Dialog open={!!prompt} onClose={() => setPrompt(null)} fullWidth maxWidth="xs">
                {prompt && (
                    <>
                        <DialogTitle>
                            {{ reject: 'Rejeitar a versão', revert: 'Republicar a versão anterior', deactivate: 'Desativar o item', schedule: 'Agendar a publicação' }[prompt.kind]}
                        </DialogTitle>
                        <DialogContent>
                            {prompt.kind === 'schedule' ? (
                                <TextField type="datetime-local" fullWidth size="small" sx={{ mt: 1 }} label="Entra no ar em (horário local)"
                                    InputLabelProps={{ shrink: true }} value={prompt.value} onChange={(e) => setPrompt({ ...prompt, value: e.target.value })} />
                            ) : (
                                <>
                                    {prompt.kind === 'deactivate' && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>O envio deste tipo para na hora. Para voltar, será preciso publicar uma versão pelo fluxo normal.</Alert>
                                    )}
                                    <TextField label="Motivo (obrigatório)" fullWidth size="small" multiline minRows={2} sx={{ mt: 1 }} autoFocus
                                        value={prompt.value} onChange={(e) => setPrompt({ ...prompt, value: e.target.value })} />
                                </>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setPrompt(null)} style={{ color: '#777' }}>Cancelar</Button>
                            <Button onClick={submitPrompt} disabled={busy || !prompt.value?.trim()} variant="contained"
                                color={prompt.kind === 'deactivate' || prompt.kind === 'reject' ? 'error' : 'primary'}
                                style={prompt.kind === 'revert' || prompt.kind === 'schedule' ? { backgroundColor: ORANGE, color: 'white' } : undefined}>
                                Confirmar
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Dialog>
    );
};

export default CatalogItemDialog;
